import { ChangesDetection } from "@adas/ng-overlay";
import { C_AdmissionList } from "../items/admission/domain/admission.model";
import { C_ChartConfigList } from "../items/charts/domain/chart-config.model";
import { I_ColorConfigResponse } from "../items/colors/color-config";
import { C_DischargeList } from "../items/discharge/domain/discharge.model";
import { C_GroupedFieldConfigList } from "../items/grouped-obs/domain/grouped-field-config.model";
import { C_DetailsInfo } from "../items/home/domain/details-config.model";
import { C_ConfigObservationList } from "../items/observation/domain/config-observation.model";
import { C_PointOfCareList } from "../items/point-of-care/domain/point-of-care-config.model";
import { C_PatientDataList } from "../items/patient/domain/patient.model";
import { C_Unit } from "../../unit/domain/unit.model";
import { I_DisplaysDataResponse, I_DisplaySelectResponse, I_DisplayConfigResponse, I_DisplayFormConfigResponse } from "../api/display.response";
import { I_CardRotatingLayout } from "../items/home/api/card-config.response";
import { C_CardInfoList } from "../items/home/domain/card-config.model";
import { I_HeaderConfigResponse } from "../items/home/api/home-config.response";
import { C_BannerList, C_HomeConfig } from "../items/home/domain/home-config.model";
import { C_GroupObsList } from "../items/grouped-obs/domain/grouped-observation.model";
import { C_ObservationWrapperList } from "../items/observation/domain/observation-wrapper.model";
import { I_GroupedObservationResponse } from "../items/grouped-obs/api/grouped-observation.response";
import { I_ObservationResponse } from "../items/observation/api/observation.response";
import { C_TreatmentList } from "../items/treatment/domain/treatment.model";
import { I_TreatmentResponse } from "../items/treatment/api/treatment.response";
import { C_AlarmList } from "../items/observation/domain/observation-alarm-data.model";
import { C_NoticeList } from "../items/notices/domain/notices.model";
import { E_DisplayType } from "../../../enums/display-type.enum";
import { E_HomeRotativeItemType } from "../../../enums/home/home-rotative-item-type.enum";
import { E_HomeRotativeItemMode } from "../../../enums/home/home-rotative-mode.enum";

export class C_DisplayDataContainer extends ChangesDetection {

  private list: Map<E_DisplayType, C_DisplayDataList> = new Map();

  constructor() {
    super();
  }

  has(type: E_DisplayType): boolean {
    return this.list.has(type);
  }

  get(type: E_DisplayType): C_DisplayDataList | undefined {
    return this.list.get(type);
  }

  set(type: E_DisplayType) {
    this.list.set(type, new C_DisplayDataList());
  }

  getValues(): C_DisplayDataList[] {
    return Array.from(this.list.values());
  }

  addNewCustom(disp: I_DisplaysDataResponse) {
    const dispType = disp.displayConfig?.type;
    if (!dispType) return;
    if (!this.list.has(dispType)) this.list.set(dispType, new C_DisplayDataList());
    this.list.get(dispType)!.setDisplay(disp);
    this.forceUpdateDetection();
  }

  reset() {
    this.list.clear();
  }
}

export class C_DisplayDataList extends ChangesDetection {

  private list: Map<string, C_DisplayData> = new Map<string, C_DisplayData>();

  constructor() {
    super();
  }

  has(type: string): boolean {
    return this.list.has(type);
  }

  get(type: string): C_DisplayData | undefined {
    return this.list.get(type);
  }

  set(type: E_DisplayType) {
    this.list.set(type, new C_DisplayData());
  }

  getValues(): C_DisplayData[] {
    return Array.from(this.list.values());
  }


  setDisplay(cust: I_DisplaysDataResponse) {
    if (this.list.has(cust.id)) return;
    this.list.set(cust.id!, new C_DisplayData(cust));
    this.forceUpdateDetection();
  }

}

export class C_DisplayData extends ChangesDetection {

  id?: string;
  unitId?: string;
  displayConfigId?: string;
  name?: string;
  pointOfCareIdList?: string[];
  unit: C_Unit = new C_Unit();
  displayInfo: C_DisplayInfo = new C_DisplayInfo();
  admissionList: C_AdmissionList = new C_AdmissionList();
  dischargeList: C_DischargeList = new C_DischargeList();
  notices: C_NoticeList = new C_NoticeList();
  patientList: C_PatientDataList = new C_PatientDataList();
  observationsList: C_ObservationWrapperList = new C_ObservationWrapperList();
  groupedObservationsList: C_GroupObsList = new C_GroupObsList();
  configObservationList: C_ConfigObservationList = new C_ConfigObservationList();
  treatmentsList: C_TreatmentList = new C_TreatmentList();
  alarmList: C_AlarmList = new C_AlarmList();

  constructor(json?: I_DisplaysDataResponse) {
    super();
    if (!json) return;
    // this.id = json.id;
    this.initialize(json);
  }

  initialize(json: I_DisplaysDataResponse) {
    if (json?.id) this.id = json.id;
    if (json?.unitId) this.unitId = json.unitId;
    if (json?.displayConfigId) this.displayConfigId = json.displayConfigId;
    if (json?.name) this.name = json.name;
    if (json?.pointOfCareIdList) this.pointOfCareIdList = json.pointOfCareIdList;
    if (json?.unit) this.unit.initialize(json.unit);
    if (json?.displayConfig) {
      this.displayInfo.initialize(json.displayConfig);
      if (json.pointOfCares) json.displayConfig.pointOfCares = json.pointOfCares
    }
    this.forceUpdateDetection();
  }

  addObservationsBulk(obs: (I_ObservationResponse & { localId: symbol })[]) {
    this.observationsList.setObservationsBulk(obs);
    this.patientList.setObservationBulk(obs);
  }

  addGroupedObservationsBulk(gObs: (I_GroupedObservationResponse & { localId: symbol })[]) {
    this.groupedObservationsList.setGroupedObservationsBulk(gObs);
    this.patientList.setGroupedObservationBulk(gObs);
  }

  addTreatmentBulk(treatments: (I_TreatmentResponse & { localId: symbol })[]) {
    this.treatmentsList.setTreatmentsBulk(treatments);
    this.patientList.setTreatmentsBulk(treatments);
  }

  resetSubs() {
    this.observationsList.clearSubs();
    this.groupedObservationsList.clearSubs();
    this.configObservationList.clearSubs();
    this.patientList.clearSubs();
  }

}

export class C_DisplayInfo extends ChangesDetection {

  id?: string;
  homeBanner: C_BannerList = new C_BannerList() 
  type?: E_DisplayType;
  homeInfo: C_HomeConfig = new C_HomeConfig();
  detailConfig: C_DetailsInfo = new C_DetailsInfo();
  headerConfig?: I_HeaderConfigResponse;
  pointOfCares: C_PointOfCareList = new C_PointOfCareList();
  formConfig: C_DisplayFormConfig = new C_DisplayFormConfig();
  colorConfig?: I_ColorConfigResponse;
  displaySectionList?: I_DisplaySelectResponse[];
  cardRotatingLayout: C_CardRotatingLayoutList = new C_CardRotatingLayoutList();
  defaultLayoutId?: string;
  layoutList: C_CardInfoList = new C_CardInfoList();
  chartConfig: C_ChartConfigList = new C_ChartConfigList();
  groupedFieldList: C_GroupedFieldConfigList = new C_GroupedFieldConfigList();


  constructor(data?: I_DisplayConfigResponse) {
    super();
    if (!data) return;
    this.initialize(data)
  }

  initialize(data: I_DisplayConfigResponse) {
    if (!data) return;
    if (data.homeBanner) this.homeBanner.reNewBanners(data.homeBanner);
    if (data.id) this.id = data.id;
    if (data.type) {
      switch (data.type) {
        case E_DisplayType.DisplayNurse:
          this.type = E_DisplayType.DisplayNurse;
          break;
        case E_DisplayType.SmartDisplay:
        default:
          this.type = E_DisplayType.SmartDisplay;
          break;
      }
    }
    // if (data.cardConfig) {
    //   this.layoutList.set(data.cardConfig);
    //   this.defaultLayoutId = data.cardConfig.id;
    // }
    if (data.homeConfig) this.homeInfo.initialize(data.homeConfig);
    if (data.detailConfig) this.detailConfig.initialize(data.detailConfig);
    if (data?.colorConfig) this.colorConfig = data?.colorConfig;
    if (data?.displaySectionList) this.displaySectionList = data?.displaySectionList;
    if (data?.headerConfig) this.headerConfig = data?.headerConfig;
    if (data?.formConfig) this.formConfig.initialize(data.formConfig)
    if (data.chartConfig) this.chartConfig.addChartConfigArrays(data.chartConfig);
    if (data.requestGroupedFieldList || data.groupedFieldList) {
      const gFieldList = data.groupedFieldList?.map(g => { return { ...g, needRequest: false } }) ?? [];
      const requestGroupedFieldList = data.requestGroupedFieldList?.map(g => { return { ...g, needRequest: true } }) ?? [];
      this.groupedFieldList.setGroupedFieldListBulk(gFieldList);
      this.groupedFieldList.setGroupedFieldListBulk(requestGroupedFieldList);
    }
    if (data.cardRotatingLayout) {
      this.defaultLayoutId = data.cardRotatingLayout[0].dataId;
      data.cardRotatingLayout.forEach((rotLayout) => {
        this.cardRotatingLayout.addCardRotatingLayout(rotLayout);
        this.layoutList.set(rotLayout.data);
      });
    }
    if (data.pointOfCares) {
      data.pointOfCares.forEach((poc) => {
        if (!poc) return;
        this.pointOfCares.setPocFromResponse(poc);
        this.pointOfCares.setPocLayoutId(poc.id, data.cardConfig?.id);
      });
    }

    this.forceUpdateDetection();
  }

  // setPatientToRotatingItems(patient: I_PatientResponse) {
  //   this.rotatingItems.forEach(rt => rt.pocList.setPatient(patient));
  // }

  // setPersonToRotatingItems(info: {
  //   id: string,
  //   person: C_Person,
  // }) {
  //   this.rotatingItems.forEach(rt => rt.pocList.setPerson(info));
  // }

  // resetSubs() {
  //   this.pointOfCares.resetSubs();
  // }
}

export class C_DisplayFormConfig extends ChangesDetection {

  admission: {
    nhc: boolean,
    bed: boolean,
    name: boolean,
    surName: boolean,
    genre: boolean,
    birthday: boolean,
    origin: boolean,
    diagnostic: boolean,
    allergy: boolean,
    insulation: boolean,
    language: boolean,
    originAux: boolean
  } = {
      nhc: false,
      bed: false,
      name: false,
      surName: false,
      genre: false,
      birthday: false,
      origin: false,
      diagnostic: false,
      allergy: false,
      insulation: false,
      language: false,
      originAux: false
    };
  demographic: {
    name: boolean,
    surName: boolean,
    genre: boolean,
    birthday: boolean,
    diagnostic: boolean,
    allergy: boolean,
    languageBarrier: boolean
  } = {
      name: false,
      surName: false,
      genre: false,
      birthday: false,
      diagnostic: false,
      allergy: false,
      languageBarrier: false,
    };
  humanization: {
    name: boolean,
    surName: boolean,
    birthday: boolean,
    language: boolean,
    bornLocation: boolean,
    residentOf: boolean,
    maritalStatus: boolean,
    familyData: boolean,
  } = {
      name: false,
      surName: false,
      birthday: false,
      language: false,
      bornLocation: false,
      residentOf: false,
      maritalStatus: false,
      familyData: false,
    };
  discharge: {
    admDischarge: boolean,
    destination: boolean,
    medicalDischarge: boolean,
    nurseDischarge: boolean,
    service: boolean,
  } = {
      admDischarge: false,
      destination: false,
      medicalDischarge: false,
      nurseDischarge: false,
      service: false,
    }
  incomeInfo: {
    origin: boolean,
    originAux: boolean,
    diagnostic: boolean,
    incomingDate: boolean,
  } = {
      origin: false,
      originAux: false,
      diagnostic: false,
      incomingDate: false
    }


  constructor(json?: I_DisplayFormConfigResponse) {
    super();
    if (json) this.initialize(json);
  }

  initialize(json: I_DisplayFormConfigResponse) {
    if (json.admission) {
      Object.entries(json.admission).forEach(([k, v]) => {
        const key = k as keyof typeof this.admission;
        this.admission[key] = !!v;
      });
    };
    if (json.demographic) {
      Object.entries(json.demographic).forEach(([k, v]) => {
        const key = k as keyof typeof this.demographic;
        this.demographic[key] = !!v;
      });
    };
    if (json.discharge) {
      Object.entries(json.discharge).forEach(([k, v]) => {
        const key = k as keyof typeof this.discharge;
        this.discharge[key] = !!v;
      });
    }
    if (json.incomeInfo) {
      Object.entries(json.incomeInfo).forEach(([k, v]) => {
        const key = k as keyof typeof this.incomeInfo;
        this.incomeInfo[key] = !!v;
      });
    }
    if (json.humanization) {
      Object.entries(json.humanization).forEach(([k, v]) => {
        const key = k as keyof typeof this.humanization;
        this.humanization[key] = !!v;
      });
    }
    this.forceUpdateDetection();
  }
}

export class C_CardRotatingLayoutItem extends ChangesDetection {
  millisecondsBeforeRotating: number = 3000;
  name?: string;
  title?: string;
  layoutId?: string;
  id: string;
  order?: number;
  mode: E_HomeRotativeItemMode = E_HomeRotativeItemMode.Default;
  type: E_HomeRotativeItemType = E_HomeRotativeItemType.HomeSection;
  constructor(info: I_CardRotatingLayout) {
    super();
    this.id = info.dataId;
    if (info.data) this.layoutId = info.data.id;
    if (info.hasOwnProperty('millisecondsBeforeRotating')) this.millisecondsBeforeRotating = info.millisecondsBeforeRotating;
    if (info.hasOwnProperty('name')) this.name = info.name;
    if (info.hasOwnProperty('title')) this.title = info.title;
    if (info.hasOwnProperty('order')) this.order = info.order;
    if (info.hasOwnProperty('type')) this.type = info.type;
    if (info.hasOwnProperty('mode')) this.mode = info.mode;
  }

}

export class C_CardRotatingLayoutList extends ChangesDetection {
  private list: Map<number, C_CardRotatingLayoutItem> = new Map<number, C_CardRotatingLayoutItem>();

  constructor() {
    super();
  }

  size() {
    return this.list.size;
  }

  get(pos: number) {
    return this.list.get(pos);
  }

  getValues(): C_CardRotatingLayoutItem[] {
    return Array.from(this.list.values());
  }

  addCardRotatingLayout(cardRotatingLayoutResponse: I_CardRotatingLayout) {
    this.list.set(cardRotatingLayoutResponse.order, new C_CardRotatingLayoutItem(cardRotatingLayoutResponse));
  }
}
